import React, { useState, useEffect, useMemo } from 'react';
import {
  getInventoryParts, createInventoryPart, updateInventoryPart, deleteInventoryPart,
  bulkUpdateInventoryParts, seedPartStockFromBill,
  lookupBarcode, setPartStock, adjustPartStock, transferPartStock, getPartMovements,
  getStores, getLocations, syncXeroItems, finalizeDraftPart,
  getUsers, getAssets, getXeroStatus,
  openPartLabelPdf, openBulkLabelsPdf, openReplenishPdf,
  getReplenishSuppliers, getEmailStatus, emailReplenishPo,
  uploadPartPhoto, deletePartPhoto, fileUrl,
} from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '../../components/ui/dialog';
import {
  Plus, AlertTriangle, Package, ScanLine, Search, Pencil, Trash2,
  MoveRight, Barcode, History, PackagePlus, PackageMinus, CheckCircle2, RefreshCw, Link2, Printer, FileDown, Mail, Send,
  CheckSquare, Percent, X, Camera,
} from 'lucide-react';
import { toast } from 'sonner';
import { QrScanner } from '../../components/QrScanner';

const emptyForm = { sku: '', name: '', description: '', barcode: '', category: 'General', unitCost: 0, sellPrice: 0, supplier: '', supplierEmail: '', minStock: 0 };

const Parts = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [parts, setParts] = useState([]);
  const [stores, setStores] = useState([]);
  const [allLocations, setAllLocations] = useState({}); // storeId -> [loc]
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const [stockOpen, setStockOpen] = useState(false);
  const [stockPart, setStockPart] = useState(null);

  const [moveOpen, setMoveOpen] = useState(false);
  const [moveData, setMoveData] = useState(null); // {part, movements}

  const [scanOpen, setScanOpen] = useState(false);

  // Bulk-edit state. `selectedIds` is a Set for O(1) toggle; the bulk
  // dialog reads it when opened. Cleared after a successful bulk update.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  // "Stock N from bill" backfill dialog state — for parts that landed in
  // inventory at qty 0 because they were approved before the auto-seed
  // fix shipped. Reuses store/location selectors and the existing
  // seedPartStockFromBill endpoint.
  const [seedPart, setSeedPart] = useState(null);
  const [seedStoreId, setSeedStoreId] = useState('');
  const [seedLocationId, setSeedLocationId] = useState('');
  const [seedSaving, setSeedSaving] = useState(false);

  const openSeedDialog = (p) => {
    setSeedPart(p);
    setSeedStoreId(stores[0]?.id || '');
    setSeedLocationId('');
  };
  const closeSeedDialog = () => { setSeedPart(null); setSeedStoreId(''); setSeedLocationId(''); };

  const submitSeed = async () => {
    if (!seedStoreId || !seedLocationId) {
      toast.error('Pick a store and location'); return;
    }
    setSeedSaving(true);
    try {
      await seedPartStockFromBill(seedPart.id, {
        storeId: seedStoreId, locationId: seedLocationId,
      });
      toast.success(`Stocked ${Math.round(seedPart.sourceBillQuantity)} × ${seedPart.name} from bill`);
      closeSeedDialog();
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to stock from bill');
    } finally {
      setSeedSaving(false);
    }
  };

  // Printable barcode label dialog
  const [labelOpen, setLabelOpen] = useState(false);
  const [labelPart, setLabelPart] = useState(null);
  const [labelCopies, setLabelCopies] = useState(1);
  const [labelPrinting, setLabelPrinting] = useState(false);

  const openLabelFor = (p) => {
    if (!(p.barcode || p.sku)) {
      toast.error('Add a barcode or SKU before printing a label.');
      return;
    }
    setLabelPart(p);
    setLabelCopies(1);
    setLabelOpen(true);
  };

  const printLabel = async () => {
    if (!labelPart) return;
    setLabelPrinting(true);
    try {
      await openPartLabelPdf(labelPart.id, Math.max(1, Math.min(70, parseInt(labelCopies, 10) || 1)));
      setLabelOpen(false);
    } catch (e) {
      toast.error('Could not generate label PDF.');
    } finally {
      setLabelPrinting(false);
    }
  };

  // Replenish-PO supplier dialog (per-supplier print + email)
  const [poOpen, setPoOpen] = useState(false);
  const [poSuppliers, setPoSuppliers] = useState([]);
  const [poLoading, setPoLoading] = useState(false);
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [emailingSupplier, setEmailingSupplier] = useState(null);
  const [emailNote, setEmailNote] = useState('');
  const [emailOverride, setEmailOverride] = useState('');
  const [emailTarget, setEmailTarget] = useState(null); // {supplier, supplierEmail}

  const openReplenishDialog = async () => {
    setPoOpen(true);
    setPoLoading(true);
    try {
      const [sups, status] = await Promise.all([getReplenishSuppliers(), getEmailStatus().catch(() => ({ configured: false }))]);
      setPoSuppliers(sups);
      setEmailConfigured(!!status.configured);
    } catch (e) {
      toast.error('Could not load supplier list.');
    } finally {
      setPoLoading(false);
    }
  };

  const sendReplenishEmail = async () => {
    if (!emailTarget) return;
    setEmailingSupplier(emailTarget.supplier);
    try {
      const res = await emailReplenishPo({
        supplier: emailTarget.supplier,
        to: emailOverride.trim() || undefined,
        note: emailNote.trim(),
      });
      toast.success(`PO sent to ${res.to} (${res.itemCount} items, £${res.estimatedTotal.toFixed(2)})`);
      setEmailTarget(null);
      setEmailNote('');
      setEmailOverride('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Email failed');
    } finally {
      setEmailingSupplier(null);
    }
  };

  // Xero drafts
  const [xeroConnected, setXeroConnected] = useState(false);
  const [syncingXero, setSyncingXero] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [finalizing, setFinalizing] = useState(null); // the draft part

  const refresh = () => getInventoryParts().then(setParts);

  useEffect(() => {
    const init = async () => {
      const [p, s, u, a, xs] = await Promise.all([
        getInventoryParts(), getStores(), getUsers(), getAssets(),
        getXeroStatus().catch(() => ({ connected: false })),
      ]);
      setParts(p);
      setStores(s);
      setCustomers(u.filter(usr => usr.role === 'customer'));
      setAssets(a);
      setXeroConnected(!!xs?.connected);
      const locsByStore = {};
      await Promise.all(s.map(async (st) => { locsByStore[st.id] = await getLocations(st.id); }));
      setAllLocations(locsByStore);
      setLoading(false);
    };
    init();
  }, []);

  const locationsFor = (storeId) => allLocations[storeId] || [];

  // Separate drafts from live parts for the two-panel layout
  const drafts = useMemo(() => parts.filter(p => p.isDraft), [parts]);
  const liveParts = useMemo(() => parts.filter(p => !p.isDraft), [parts]);

  const filtered = useMemo(() => {
    if (!search.trim()) return liveParts;
    const q = search.toLowerCase();
    return liveParts.filter(p =>
      p.name.toLowerCase().includes(q) ||
      (p.sku || '').toLowerCase().includes(q) ||
      (p.barcode || '').includes(q) ||
      (p.category || '').toLowerCase().includes(q) ||
      (p.customerName || '').toLowerCase().includes(q) ||
      (p.assetName || '').toLowerCase().includes(q)
    );
  }, [liveParts, search]);

  // Select-all for the currently-filtered view (respects search). Mixed
  // state = some-but-not-all selected → clicking selects everything.
  const filteredIds = useMemo(() => filtered.map(p => p.id), [filtered]);
  const allVisibleSelected = filteredIds.length > 0 && filteredIds.every(id => selectedIds.has(id));
  const someVisibleSelected = filteredIds.some(id => selectedIds.has(id));
  const toggleSelectAllVisible = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        filteredIds.forEach(id => next.delete(id));
      } else {
        filteredIds.forEach(id => next.add(id));
      }
      return next;
    });
  };

  const syncFromXero = async () => {
    setSyncingXero(true);
    try {
      const res = await syncXeroItems();
      toast.success(`Xero sync: ${res.imported} imported as drafts, ${res.updated} updated`);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Xero sync failed');
    } finally {
      setSyncingXero(false);
    }
  };

  // ---- CRUD ----
  const openCreate = () => { setEditing(null); setForm(emptyForm); setEditOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      sku: p.sku || '', name: p.name, description: p.description || '', barcode: p.barcode || '',
      category: p.category || 'General',
      unitCost: p.unitCost || 0,
      sellPrice: p.sellPrice ?? p.unitCost ?? 0,
      supplier: p.supplier || '',
      minStock: p.minStock || 0,
    });
    setEditOpen(true);
  };

  const savePart = async () => {
    if (!form.sku.trim() || !form.name.trim()) return toast.error('SKU and name required');
    try {
      const payload = {
        ...form,
        unitCost: Number(form.unitCost) || 0,
        sellPrice: Number(form.sellPrice) || 0,
        minStock: Number(form.minStock) || 0,
      };
      if (editing) await updateInventoryPart(editing.id, payload);
      else await createInventoryPart(payload);
      toast.success(editing ? 'Part updated' : 'Part created');
      setEditOpen(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const removePart = async (p) => {
    if (!window.confirm(`Delete ${p.name}? All stock rows will be removed.`)) return;
    try {
      await deleteInventoryPart(p.id);
      toast.success('Part deleted');
      await refresh();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  // Part photo upload — only available when editing an existing part
  // (photos hang off the part doc, so we need an id to attach to).
  const [photoUploading, setPhotoUploading] = useState(false);
  const handlePhotoUpload = async (file) => {
    if (!editing || !file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please pick an image file');
      return;
    }
    setPhotoUploading(true);
    try {
      const info = await uploadPartPhoto(editing.id, file);
      setEditing({ ...editing, photoUrl: info.url, photo: info });
      setParts((list) => list.map(p =>
        p.id === editing.id ? { ...p, photoUrl: info.url, photo: info } : p
      ));
      toast.success('Photo updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setPhotoUploading(false);
    }
  };
  const handlePhotoRemove = async () => {
    if (!editing) return;
    setPhotoUploading(true);
    try {
      await deletePartPhoto(editing.id);
      setEditing({ ...editing, photoUrl: undefined, photo: undefined });
      setParts((list) => list.map(p =>
        p.id === editing.id ? { ...p, photoUrl: undefined, photo: undefined } : p
      ));
      toast.success('Photo removed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Remove failed');
    } finally {
      setPhotoUploading(false);
    }
  };


  // ---- Scan ----
  const onScan = async (code) => {
    setScanOpen(false);
    try {
      const part = await lookupBarcode(code);
      toast.success(`Found: ${part.name}`);
      setStockPart(part);
      setStockOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `No part for "${code}"`);
    }
  };

  // ---- Movements ----
  const openMovements = async (p) => {
    try {
      const movs = await getPartMovements(p.id);
      setMoveData({ part: p, movements: movs });
      setMoveOpen(true);
    } catch (e) {
      toast.error('Failed to load history');
    }
  };

  const lowCount = parts.filter(p => p.low).length;
  const totalStock = parts.reduce((a, p) => a + (p.totalStock || 0), 0);

  if (loading) return <div className="p-8 text-slate-500">Loading inventory...</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageHeader
        title="Parts Inventory"
        description="Multi-store stock levels, barcodes, and movement history."
        actions={
          <>
            {isAdmin && xeroConnected && (
              <Button variant="outline" size="sm" onClick={syncFromXero} disabled={syncingXero} data-testid="sync-xero-items-btn" title="Sync items from Xero">
                <RefreshCw className={`h-4 w-4 sm:mr-2 ${syncingXero ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">{syncingXero ? 'Syncing...' : 'Sync from Xero'}</span>
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => setScanOpen(true)} data-testid="scan-barcode-btn" title="Scan a barcode">
              <ScanLine className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Scan Barcode</span>
            </Button>
            {isAdmin && liveParts.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    await openBulkLabelsPdf(liveParts.filter(p => p.barcode || p.sku).map(p => p.id));
                  } catch (e) {
                    toast.error('Could not generate label sheet.');
                  }
                }}
                data-testid="print-all-labels-btn"
                title="Print a single PDF with labels for every part"
              >
                <Printer className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Print all labels</span>
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={openReplenishDialog}
                data-testid="replenish-pdf-btn"
                title="Print or email a purchase order for any supplier with low stock"
              >
                <FileDown className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Replenish PO</span>
              </Button>
            )}
            {isAdmin && (
              <Button size="sm" onClick={openCreate} className="bg-slate-900 hover:bg-slate-800" data-testid="new-part-btn">
                <Plus className="h-4 w-4 mr-2" /> Add Part
              </Button>
            )}
          </>
        }
      />

      {/* Xero Drafts section */}
      {drafts.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-md">
          <div className="flex items-center justify-between px-4 py-3 border-b border-amber-200">
            <div className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-amber-700" />
              <div>
                <div className="font-display uppercase text-sm text-amber-900 tracking-wide">
                  {drafts.length} item{drafts.length === 1 ? '' : 's'} waiting from Xero
                </div>
                <div className="text-xs text-amber-800">Finalise each draft by choosing a store, location, and optional customer/asset.</div>
              </div>
            </div>
          </div>
          <div className="divide-y divide-amber-200">
            {drafts.map(d => (
              <div key={d.id} data-testid={`draft-row-${d.id}`} className="flex items-center justify-between px-4 py-3">
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate">{d.name}</div>
                  <div className="text-xs text-slate-600 flex items-center gap-3 mt-0.5">
                    <span className="font-mono">{d.xeroCode || d.sku}</span>
                    <span>£{(d.sellPrice ?? d.unitCost ?? 0).toFixed(2)} sell{d.costPrice > 0 && d.costPrice !== d.sellPrice ? ` · £${d.costPrice.toFixed(2)} cost` : ''}</span>
                    {d.xeroIsTracked ? <span>Xero QOH: {d.xeroQuantityOnHand}</span> : <span className="text-slate-400 italic">Not tracked in Xero</span>}
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => { setFinalizing(d); setFinalizeOpen(true); }}
                  className="bg-slate-900 hover:bg-slate-800"
                  data-testid={`finalize-${d.id}`}
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" /> Finalise
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stat strip */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500">Live parts</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{liveParts.length}</div>
          {drafts.length > 0 && <div className="text-xs text-amber-700 mt-0.5">+ {drafts.length} drafts</div>}
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500">Total units</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{totalStock.toLocaleString()}</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-md p-4">
          <div className="text-xs uppercase tracking-wider text-slate-500">Stores</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{stores.length}</div>
        </div>
        <div className="bg-white border border-red-200 rounded-md p-4">
          <div className="text-xs uppercase tracking-wider text-red-600">Low-stock alerts</div>
          <div className="text-2xl font-bold text-red-700 mt-1">{lowCount}</div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="parts-search-input"
            placeholder="Search by name, SKU, barcode..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 bg-white"
          />
        </div>
      </div>

      {/* Desktop: full table */}
      <div className="hidden lg:block bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left">
              {isAdmin && (
                <th className="pl-4 pr-0 py-3 w-8">
                  <Checkbox
                    checked={allVisibleSelected}
                    onCheckedChange={toggleSelectAllVisible}
                    data-testid="parts-select-all"
                    aria-label="Select all visible parts"
                  />
                </th>
              )}
              <th className="px-4 py-3 font-medium text-slate-600">Part</th>
              <th className="px-4 py-3 font-medium text-slate-600 hidden md:table-cell">Barcode / SKU</th>
              <th className="px-4 py-3 font-medium text-slate-600 hidden sm:table-cell">Total stock</th>
              <th className="px-4 py-3 font-medium text-slate-600 hidden lg:table-cell">Breakdown</th>
              <th className="px-4 py-3 font-medium text-slate-600 text-right md:text-left">Sell price</th>
              <th className="px-4 py-3 font-medium text-slate-600 hidden sm:table-cell">Status</th>
              <th className="px-4 py-3 font-medium text-slate-600 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.map(p => (
              <tr key={p.id} data-testid={`part-row-${p.id}`} className={`hover:bg-slate-50 align-top ${selectedIds.has(p.id) ? 'bg-blue-50' : ''}`}>
                {isAdmin && (
                  <td className="pl-4 pr-0 py-3 w-8">
                    <Checkbox
                      checked={selectedIds.has(p.id)}
                      onCheckedChange={() => toggleSelect(p.id)}
                      data-testid={`part-select-${p.id}`}
                    />
                  </td>
                )}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {p.photoUrl ? (
                      <img
                        src={fileUrl(p.photoUrl)}
                        alt={p.name}
                        className="h-10 w-10 rounded object-cover border border-slate-200 shrink-0"
                        data-testid={`part-photo-${p.id}`}
                      />
                    ) : (
                      <div className="h-9 w-9 bg-slate-100 rounded flex items-center justify-center shrink-0">
                        <Package className="h-4 w-4 text-slate-700" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 truncate">{p.name}</div>
                      <div className="text-xs text-slate-500 truncate">{p.category}</div>
                      <div className="md:hidden text-[11px] text-slate-500 font-mono mt-0.5 truncate">{p.sku}</div>
                      <div className="sm:hidden mt-1 text-xs">
                        <span className={`font-semibold ${p.lowGlobal ? 'text-red-600' : 'text-slate-900'}`}>{p.totalStock} in stock</span>
                        {p.low && <span className="ml-2 text-red-600">(low)</span>}
                      </div>
                      {(p.customerName || p.assetName) && (
                        <div className="text-xs text-indigo-700 mt-0.5 truncate">
                          {p.customerName && <span>for {p.customerName}</span>}
                          {p.assetName && <span> · asset {p.assetName}</span>}
                        </div>
                      )}
                      {p.xeroItemId && <div className="text-xs text-blue-700 mt-0.5">Synced from Xero</div>}
                      {isAdmin && p.sourceBillId && p.sourceBillQuantity > 0
                        && !p.billStockSeeded && p.totalStock === 0 && (
                          <button
                            type="button"
                            onClick={() => openSeedDialog(p)}
                            data-testid={`seed-bill-${p.id}`}
                            className="mt-1.5 inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] rounded-full bg-amber-100 text-amber-900 border border-amber-200 hover:bg-amber-200 transition-colors"
                          >
                            <PackagePlus className="h-3 w-3" />
                            Stock {Math.round(p.sourceBillQuantity)} from bill
                          </button>
                        )}
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 hidden md:table-cell">
                  <div className="font-mono text-xs text-slate-700 flex items-center gap-1">
                    <Barcode className="h-3 w-3" /> {p.barcode || '—'}
                  </div>
                  <div className="text-xs text-slate-500">{p.sku}</div>
                </td>
                <td className="px-4 py-3 hidden sm:table-cell">
                  <div className={`font-semibold ${p.lowGlobal ? 'text-red-600' : 'text-slate-900'}`}>{p.totalStock}</div>
                  <div className="text-xs text-slate-500">min {p.minStock || 0}</div>
                </td>
                <td className="px-4 py-3 hidden lg:table-cell">
                  {p.stock.length === 0
                    ? <span className="text-xs text-slate-400 italic">Not stocked yet</span>
                    : (
                      <div className="space-y-1">
                        {p.stock.map(s => {
                          const low = s.minStock > 0 && s.quantity < s.minStock;
                          return (
                            <div key={s.id} className="text-xs flex items-center gap-2">
                              <span className="font-semibold text-slate-900 w-8 text-right">{s.quantity}</span>
                              <span className="text-slate-500">×</span>
                              <span className="text-slate-700">{s.storeName} / {s.locationName}</span>
                              {low && <span className="text-red-600 font-medium">(low)</span>}
                            </div>
                          );
                        })}
                      </div>
                    )
                  }
                </td>
                <td className="px-4 py-3 text-slate-700 text-right md:text-left">
                  <div>£{(p.sellPrice ?? p.unitCost ?? 0).toFixed(2)}</div>
                  {p.costPrice > 0 && p.costPrice !== p.sellPrice && (
                    <div className="text-xs text-slate-500">cost £{p.costPrice.toFixed(2)}</div>
                  )}
                </td>
                <td className="px-4 py-3 hidden sm:table-cell">
                  {p.low
                    ? <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 px-2 py-1 rounded"><AlertTriangle className="h-3 w-3" /> Low</span>
                    : <span className="inline-flex text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded">In stock</span>
                  }
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => { setStockPart(p); setStockOpen(true); }} data-testid={`manage-stock-${p.id}`} title="Manage stock">
                      <PackagePlus className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openMovements(p)} data-testid={`history-${p.id}`} title="History">
                      <History className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => openLabelFor(p)} data-testid={`print-label-${p.id}`} title="Print barcode label">
                      <Printer className="h-4 w-4" />
                    </Button>
                    {isAdmin && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`edit-part-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => removePart(p)} data-testid={`delete-part-${p.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={isAdmin ? 8 : 7} className="px-4 py-12 text-center text-slate-500">No parts match your search.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile: card stack — the desktop table is 7 cols wide and overflows
          the viewport on phones, hiding the Edit/Delete icons off-screen to
          the right. Cards put every action one tap away. */}
      <div className="lg:hidden space-y-2.5">
        {filtered.length === 0 && (
          <div className="bg-white border border-dashed border-slate-200 rounded-md p-10 text-center text-slate-500">
            No parts match your search.
          </div>
        )}
        {filtered.map(p => (
          <div
            key={p.id}
            data-testid={`part-card-${p.id}`}
            className={`bg-white border rounded-md p-3.5 ${selectedIds.has(p.id) ? 'border-blue-400 bg-blue-50/40' : 'border-slate-200'}`}
          >
            <div className="flex items-start gap-3">
              {isAdmin && (
                <Checkbox
                  checked={selectedIds.has(p.id)}
                  onCheckedChange={() => toggleSelect(p.id)}
                  data-testid={`part-select-mobile-${p.id}`}
                  className="mt-1"
                />
              )}
              <div className="h-10 w-10 bg-slate-100 rounded flex items-center justify-center shrink-0 overflow-hidden">
                {p.photoUrl ? (
                  <img
                    src={fileUrl(p.photoUrl)}
                    alt={p.name}
                    className="h-full w-full object-cover"
                    data-testid={`part-photo-mobile-${p.id}`}
                  />
                ) : (
                  <Package className="h-4.5 w-4.5 text-slate-700" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">{p.name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-1.5 flex-wrap mt-0.5">
                      <span>{p.category}</span>
                      {p.sku && <span className="font-mono text-slate-400">· {p.sku}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-semibold text-slate-900 text-sm">£{(p.sellPrice ?? p.unitCost ?? 0).toFixed(2)}</div>
                    {p.costPrice > 0 && p.costPrice !== p.sellPrice && (
                      <div className="text-[10px] text-slate-500">cost £{p.costPrice.toFixed(2)}</div>
                    )}
                  </div>
                </div>
                {p.barcode && (
                  <div className="text-[11px] text-slate-500 font-mono mt-1 flex items-center gap-1">
                    <Barcode className="h-3 w-3" /> {p.barcode}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                  <span className={`text-xs font-semibold ${p.lowGlobal ? 'text-red-600' : 'text-slate-900'}`}>
                    {p.totalStock} in stock
                  </span>
                  {p.low
                    ? <span className="inline-flex items-center gap-1 text-[10px] font-medium text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded"><AlertTriangle className="h-2.5 w-2.5" /> Low</span>
                    : <span className="inline-flex text-[10px] font-medium text-green-700 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded">OK</span>
                  }
                  {p.xeroItemId && <span className="text-[10px] text-blue-700">Xero</span>}
                </div>
                {(p.customerName || p.assetName) && (
                  <div className="text-[11px] text-indigo-700 mt-1 truncate">
                    {p.customerName && <span>for {p.customerName}</span>}
                    {p.assetName && <span> · {p.assetName}</span>}
                  </div>
                )}
                {/* Bill-derived part still at zero stock — admin approved
                    before the auto-seed fix shipped. Offer a one-tap
                    backfill that reads the bill's qty and creates the
                    stock row. */}
                {isAdmin && p.sourceBillId && p.sourceBillQuantity > 0
                  && !p.billStockSeeded && p.totalStock === 0 && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); openSeedDialog(p); }}
                      data-testid={`seed-bill-mobile-${p.id}`}
                      className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full bg-amber-100 text-amber-900 border border-amber-200 hover:bg-amber-200 transition-colors"
                    >
                      <PackagePlus className="h-3 w-3" />
                      Stock {Math.round(p.sourceBillQuantity)} from bill
                    </button>
                  )}
              </div>
            </div>

            {/* Actions row — split into primary (edit — big CTA for admins) and
                secondary (icons). Matches the "click anywhere" fix used on
                Work Orders so users don't hunt for tiny icons. */}
            <div className="flex items-center gap-1 mt-3 pt-2.5 border-t border-slate-100">
              {isAdmin && (
                <Button
                  size="sm"
                  variant="outline"
                  className="flex-1"
                  onClick={() => openEdit(p)}
                  data-testid={`edit-part-mobile-${p.id}`}
                >
                  <Pencil className="h-4 w-4 mr-1.5" /> Edit
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setStockPart(p); setStockOpen(true); }}
                data-testid={`manage-stock-mobile-${p.id}`}
                title="Manage stock"
              >
                <PackagePlus className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => openMovements(p)}
                data-testid={`history-mobile-${p.id}`}
                title="History"
              >
                <History className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => openLabelFor(p)}
                data-testid={`print-label-mobile-${p.id}`}
                title="Print label"
              >
                <Printer className="h-4 w-4" />
              </Button>
              {isAdmin && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => removePart(p)}
                  data-testid={`delete-part-mobile-${p.id}`}
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Part */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Part' : 'Create Part'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Photo (only available after the part is created — needs an id) */}
            {editing && (
              <div>
                <Label>Photo</Label>
                <div className="mt-1.5">
                  {editing.photoUrl ? (
                    <div className="relative aspect-[4/3] bg-slate-100 rounded-md overflow-hidden border border-slate-200">
                      <img
                        src={fileUrl(editing.photoUrl)}
                        alt={editing.name}
                        className="absolute inset-0 w-full h-full object-cover"
                        data-testid="part-photo-preview"
                      />
                      <div className="absolute top-2 right-2 flex gap-1.5">
                        <label className="cursor-pointer bg-white/95 backdrop-blur border border-slate-200 rounded-md p-1.5 hover:bg-white shadow-sm" title="Replace photo">
                          <Camera className="h-4 w-4 text-slate-700" />
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handlePhotoUpload(e.target.files?.[0])}
                            disabled={photoUploading}
                            data-testid="replace-part-photo-input"
                          />
                        </label>
                        <button
                          type="button"
                          onClick={handlePhotoRemove}
                          disabled={photoUploading}
                          className="bg-white/95 backdrop-blur border border-slate-200 rounded-md p-1.5 hover:bg-rose-50 hover:border-rose-200 shadow-sm"
                          title="Remove photo"
                          data-testid="remove-part-photo-btn"
                        >
                          <X className="h-4 w-4 text-rose-600" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <label
                      className="flex flex-col items-center justify-center aspect-[4/3] bg-slate-50 border-2 border-dashed border-slate-200 rounded-md hover:border-blue-700 hover:bg-blue-50 transition-colors cursor-pointer"
                      data-testid="upload-part-photo-label"
                    >
                      <Camera className="h-7 w-7 text-slate-400 mb-1" />
                      <span className="text-sm text-slate-600">{photoUploading ? 'Uploading...' : 'Tap to add a photo'}</span>
                      <span className="text-xs text-slate-400 mt-0.5">JPG, PNG, GIF or WebP · max 10 MB</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handlePhotoUpload(e.target.files?.[0])}
                        disabled={photoUploading}
                        data-testid="upload-part-photo-input"
                      />
                    </label>
                  )}
                </div>
              </div>
            )}
            {!editing && (
              <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                <Camera className="inline h-3.5 w-3.5 mr-1 -mt-0.5 text-slate-400" />
                Save the part first, then come back to add a photo.
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>SKU</Label><Input data-testid="part-sku-input" className="mt-1.5" value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })} placeholder="e.g. LTM-BRG-6205" /></div>
              <div>
                <Label>Barcode</Label>
                <div className="mt-1.5 flex gap-2">
                  <Input data-testid="part-barcode-input" value={form.barcode} onChange={e => setForm({ ...form, barcode: e.target.value })} placeholder="Scan or type" />
                  <Button type="button" variant="outline" size="icon" onClick={() => setScanOpen(true)} data-testid="scan-into-part-btn">
                    <ScanLine className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
            <div><Label>Name</Label><Input data-testid="part-name-input" className="mt-1.5" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Description</Label><Textarea data-testid="part-desc-input" className="mt-1.5" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} /></div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div><Label>Category</Label><Input data-testid="part-category-input" className="mt-1.5" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} /></div>
              <div><Label>Cost price (£)</Label><Input data-testid="part-cost-input" className="mt-1.5" type="number" step="0.01" value={form.unitCost} onChange={e => setForm({ ...form, unitCost: e.target.value })} /></div>
              <div><Label>Sell price (£)</Label><Input data-testid="part-sell-input" className="mt-1.5" type="number" step="0.01" value={form.sellPrice} onChange={e => setForm({ ...form, sellPrice: e.target.value })} /></div>
              <div><Label>Global min stock</Label><Input data-testid="part-minstock-input" className="mt-1.5" type="number" value={form.minStock} onChange={e => setForm({ ...form, minStock: e.target.value })} /></div>
            </div>
            <div>
              <Label>Supplier <span className="text-xs text-slate-500 font-normal">(optional · used to group Replenishment POs)</span></Label>
              <Input data-testid="part-supplier-input" className="mt-1.5" placeholder="e.g. RS Components, Acme Ltd" value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })} />
            </div>
            <div>
              <Label>Supplier email <span className="text-xs text-slate-500 font-normal">(optional · used by &quot;Email PO&quot;)</span></Label>
              <Input data-testid="part-supplier-email-input" type="email" className="mt-1.5" placeholder="orders@supplier.com" value={form.supplierEmail} onChange={e => setForm({ ...form, supplierEmail: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={savePart} className="bg-slate-900 hover:bg-slate-800" data-testid="save-part-btn">{editing ? 'Save changes' : 'Create part'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stock management */}
      <StockManager
        open={stockOpen}
        onClose={() => setStockOpen(false)}
        part={stockPart}
        stores={stores}
        locationsFor={locationsFor}
        isAdmin={isAdmin}
        onRefresh={async () => {
          await refresh();
          if (stockPart) {
            const updated = (await getInventoryParts()).find(x => x.id === stockPart.id);
            setStockPart(updated);
          }
        }}
      />

      {/* Replenish PO supplier picker */}
      <Dialog open={poOpen} onOpenChange={setPoOpen}>
        <DialogContent className="max-w-2xl" data-testid="replenish-dialog">
          <DialogHeader>
            <DialogTitle>Replenishment purchase orders</DialogTitle>
            <DialogDescription>
              Print or email a per-supplier PO. Suggested order qty restocks each part to 2× its minimum.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {poLoading && <div className="text-sm text-slate-500">Loading suppliers...</div>}
            {!poLoading && poSuppliers.length === 0 && (
              <div className="text-sm text-slate-500 p-6 text-center bg-slate-50 rounded">
                Nothing to reorder right now — every part is above its minimum stock.
              </div>
            )}
            {!poLoading && poSuppliers.length > 0 && (
              <>
                {!emailConfigured && (
                  <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded px-3 py-2">
                    Email is not configured yet. Set <code className="font-mono">RESEND_API_KEY</code> in <code>backend/.env</code> to unlock the &ldquo;Email&rdquo; buttons.
                  </div>
                )}
                <div className="border border-slate-200 rounded divide-y divide-slate-100">
                  {poSuppliers.map(s => (
                    <div
                      key={s.supplier}
                      className="flex items-center gap-3 px-4 py-3"
                      data-testid={`po-supplier-row-${s.supplier}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900 truncate">{s.supplier}</div>
                        <div className="text-xs text-slate-500">
                          {s.count} item{s.count === 1 ? '' : 's'} · est. £{s.estimatedTotal.toFixed(2)}
                          {s.supplierEmail && (
                            <span className="ml-2 text-slate-400">· {s.supplierEmail}</span>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openReplenishPdf(s.supplier).catch(() => toast.error('Print failed'))}
                        data-testid={`po-print-${s.supplier}`}
                      >
                        <Printer className="h-4 w-4 mr-2" /> Print
                      </Button>
                      <Button
                        size="sm"
                        disabled={!emailConfigured || s.supplier === 'Unassigned supplier'}
                        onClick={() => {
                          setEmailTarget(s);
                          setEmailOverride('');
                          setEmailNote('');
                        }}
                        data-testid={`po-email-${s.supplier}`}
                        className="bg-slate-900 hover:bg-slate-800 text-white"
                        title={
                          !emailConfigured ? 'Email not configured'
                          : s.supplier === 'Unassigned supplier' ? 'Set a supplier on these parts first'
                          : (s.supplierEmail ? `Send to ${s.supplierEmail}` : 'No saved email — you can enter one')
                        }
                      >
                        <Mail className="h-4 w-4 mr-2" /> Email
                      </Button>
                    </div>
                  ))}
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => openReplenishPdf().catch(() => toast.error('Print failed'))}
                  data-testid="po-print-all-btn"
                >
                  <Printer className="h-4 w-4 mr-2" /> Print combined PO (all suppliers)
                </Button>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPoOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email PO confirm + optional note */}
      <Dialog open={!!emailTarget} onOpenChange={(o) => !o && setEmailTarget(null)}>
        <DialogContent data-testid="email-po-dialog">
          <DialogHeader>
            <DialogTitle>Email PO to {emailTarget?.supplier}</DialogTitle>
            <DialogDescription>
              Sends the supplier-filtered PDF as an attachment. The admin email will be CC'd by default.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Recipient email</Label>
              <Input
                type="email"
                className="mt-1.5"
                value={emailOverride}
                onChange={(e) => setEmailOverride(e.target.value)}
                placeholder={emailTarget?.supplierEmail || 'orders@supplier.com'}
                data-testid="email-po-recipient"
              />
              {emailTarget?.supplierEmail && !emailOverride && (
                <p className="text-xs text-slate-500 mt-1">
                  Will send to saved address: <span className="font-mono">{emailTarget.supplierEmail}</span>
                </p>
              )}
            </div>
            <div>
              <Label>Optional note</Label>
              <Textarea
                rows={3}
                className="mt-1.5"
                value={emailNote}
                onChange={(e) => setEmailNote(e.target.value)}
                placeholder="e.g. urgent — site outage Friday morning"
                data-testid="email-po-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailTarget(null)}>Cancel</Button>
            <Button
              onClick={sendReplenishEmail}
              disabled={
                emailingSupplier === emailTarget?.supplier ||
                (!emailOverride.trim() && !emailTarget?.supplierEmail)
              }
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="email-po-send-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              {emailingSupplier === emailTarget?.supplier ? 'Sending...' : 'Send PO'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Print barcode label */}
      <Dialog open={labelOpen} onOpenChange={setLabelOpen}>
        <DialogContent data-testid="label-dialog">
          <DialogHeader>
            <DialogTitle>Print barcode label</DialogTitle>
            <DialogDescription>
              {labelPart ? (
                <span>
                  {labelPart.name} <span className="font-mono text-xs text-slate-500">({labelPart.sku})</span>
                </span>
              ) : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-xs text-slate-500">
              Code 128 barcode · {labelPart?.barcode || labelPart?.sku || labelPart?.id}
            </div>
            <div>
              <Label>Copies (1 – 70)</Label>
              <Input
                data-testid="label-copies-input"
                type="number"
                min={1}
                max={70}
                className="mt-1.5"
                value={labelCopies}
                onChange={e => setLabelCopies(e.target.value)}
              />
              <p className="text-xs text-slate-500 mt-1">
                1 copy = single-label layout. 2+ copies = 2×7 grid on A4.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLabelOpen(false)}>Cancel</Button>
            <Button
              onClick={printLabel}
              disabled={labelPrinting}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="label-print-btn"
            >
              <Printer className="h-4 w-4 mr-2" />
              {labelPrinting ? 'Generating...' : 'Open PDF'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Movements */}
      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Movement history · {moveData?.part?.name}</DialogTitle></DialogHeader>
          {moveData && (
            <div className="space-y-2 text-sm">
              {moveData.movements.length === 0 && <div className="text-slate-500">No movements yet.</div>}
              {moveData.movements.map(m => (
                <div key={m.id} className="flex items-center justify-between border-b border-slate-100 py-2">
                  <div>
                    <div className="font-medium text-slate-900">
                      {m.qty > 0 ? '+' : ''}{m.qty} · {m.reason}
                    </div>
                    <div className="text-xs text-slate-500">
                      {m.storeName} / {m.locationName} · {m.userName || 'system'}
                      {m.workOrderId && <> · WO {m.workOrderId}</>}
                    </div>
                  </div>
                  <div className="text-xs text-slate-500">{new Date(m.timestamp).toLocaleString('en-GB')}</div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Scanner */}
      <QrScanner
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onScan={onScan}
        title="Scan Part Barcode"
        hint="Point your camera at a part's barcode or QR code."
      />

      {/* Floating bulk-action bar — only shown when admin has parts selected.
          Sticky-bottom so it follows while scrolling long lists. On mobile
          we give it a safe-area inset so it sits above the home-bar. */}
      {isAdmin && selectedIds.size > 0 && (
        <div
          className="fixed left-1/2 -translate-x-1/2 bottom-4 z-40 bg-slate-900 text-white rounded-full shadow-lg border border-slate-700 px-2 py-2 flex items-center gap-2 text-sm"
          style={{ bottom: 'max(1rem, env(safe-area-inset-bottom))' }}
          data-testid="parts-bulk-bar"
        >
          <div className="inline-flex items-center gap-2 px-3">
            <CheckSquare className="h-4 w-4" />
            <span><span className="font-semibold">{selectedIds.size}</span> selected</span>
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setBulkOpen(true)}
            data-testid="parts-bulk-edit-btn"
          >
            <Pencil className="h-3.5 w-3.5 mr-1.5" /> Bulk edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={clearSelection}
            className="text-slate-300 hover:text-white hover:bg-slate-800"
            data-testid="parts-bulk-clear"
            aria-label="Clear selection"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      <BulkEditPartsDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        count={selectedIds.size}
        onSubmit={async (patch) => {
          try {
            const res = await bulkUpdateInventoryParts({
              ids: Array.from(selectedIds),
              ...patch,
            });
            toast.success(`Updated ${res.updated} of ${res.requested} parts`);
            setBulkOpen(false);
            clearSelection();
            await refresh();
          } catch (e) {
            toast.error(e?.response?.data?.detail || 'Bulk update failed');
          }
        }}
      />

      {/* Backfill dialog — fixes parts approved before the auto-seed fix
          (the user's "Toolstation bill items at qty 0" case). */}
      <Dialog open={!!seedPart} onOpenChange={(o) => !o && closeSeedDialog()}>
        <DialogContent data-testid="seed-from-bill-dialog">
          <DialogHeader>
            <DialogTitle>Stock from bill</DialogTitle>
            <DialogDescription>
              {seedPart && (
                <>
                  Add <span className="font-semibold">{Math.round(seedPart.sourceBillQuantity)}</span> ×{' '}
                  <span className="font-semibold">{seedPart.name}</span> to a store location. We read this number
                  from the bill that originally created this part.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Store</Label>
              <Select value={seedStoreId} onValueChange={(v) => { setSeedStoreId(v); setSeedLocationId(''); }}>
                <SelectTrigger className="mt-1" data-testid="seed-store-select">
                  <SelectValue placeholder="Pick a store…" />
                </SelectTrigger>
                <SelectContent>
                  {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Location</Label>
              <Select
                value={seedLocationId}
                onValueChange={setSeedLocationId}
                disabled={!seedStoreId}
              >
                <SelectTrigger className="mt-1" data-testid="seed-location-select">
                  <SelectValue placeholder={seedStoreId ? 'Pick a location…' : 'Pick a store first'} />
                </SelectTrigger>
                <SelectContent>
                  {(allLocations[seedStoreId] || []).map(l => (
                    <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeSeedDialog} disabled={seedSaving}>Cancel</Button>
            <Button
              onClick={submitSeed}
              disabled={seedSaving || !seedStoreId || !seedLocationId}
              data-testid="seed-confirm-btn"
              className="bg-amber-700 hover:bg-amber-800 text-white"
            >
              {seedSaving ? 'Stocking…' : `Stock ${seedPart ? Math.round(seedPart.sourceBillQuantity) : 0}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Finalize Xero draft dialog */}
      <FinalizeDraftDialog
        open={finalizeOpen}
        onClose={() => { setFinalizeOpen(false); setFinalizing(null); }}
        draft={finalizing}
        stores={stores}
        locationsFor={locationsFor}
        customers={customers}
        assets={assets}
        onSaved={async () => {
          setFinalizeOpen(false);
          setFinalizing(null);
          await refresh();
        }}
      />
    </div>
  );
};

// -------- Stock manager dialog --------
const StockManager = ({ open, onClose, part, stores, locationsFor, isAdmin, onRefresh }) => {
  const [mode, setMode] = useState('adjust'); // adjust | add | transfer
  const [storeId, setStoreId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [qty, setQty] = useState(1);
  const [minStock, setMinStock] = useState(0);
  const [reason, setReason] = useState('restock');

  const [toStoreId, setToStoreId] = useState('');
  const [toLocationId, setToLocationId] = useState('');

  useEffect(() => {
    if (!open) return;
    // For brand-new parts with no stock anywhere, default to "Set level"
    // — Adjust would otherwise need a row to exist first. (The backend
    // now also auto-creates rows on positive-delta admin Adjust as a
    // safety net, but defaulting the UI here is clearer.)
    const hasAnyStock = (part?.stock?.length || 0) > 0;
    setMode(hasAnyStock ? 'adjust' : 'add');
    setStoreId(part?.stock?.[0]?.storeId || stores[0]?.id || '');
    setLocationId(part?.stock?.[0]?.locationId || '');
    setQty(1);
    setMinStock(0);
    setReason('restock');
    setToStoreId('');
    setToLocationId('');
  }, [open, part, stores]);

  if (!part) return null;

  const submit = async () => {
    try {
      if (mode === 'adjust') {
        if (!storeId || !locationId) return toast.error('Pick a location');
        if (qty === 0) return toast.error('Qty cannot be 0');
        await adjustPartStock(part.id, { storeId, locationId, delta: Number(qty), reason });
      } else if (mode === 'add') {
        if (!isAdmin) return toast.error('Admins only');
        if (!storeId || !locationId) return toast.error('Pick a location');
        await setPartStock(part.id, { storeId, locationId, quantity: Number(qty), minStock: Number(minStock) });
      } else if (mode === 'transfer') {
        if (!storeId || !locationId || !toStoreId || !toLocationId) return toast.error('Pick both locations');
        await transferPartStock(part.id, {
          fromStoreId: storeId, fromLocationId: locationId,
          toStoreId, toLocationId, qty: Number(qty), reason: 'transfer',
        });
      }
      toast.success('Stock updated');
      await onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  const currentQty = part.stock?.find(s => s.storeId === storeId && s.locationId === locationId)?.quantity;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage stock · {part.name}</DialogTitle>
        </DialogHeader>

        {/* Current breakdown */}
        <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Current stock</div>
          {part.stock.length === 0 && <div className="text-sm text-slate-500">Not stocked anywhere yet.</div>}
          <div className="space-y-1 text-sm">
            {part.stock.map(s => (
              <div key={s.id} className="flex items-center justify-between">
                <span className="text-slate-700">{s.storeName} / {s.locationName}</span>
                <span className="font-semibold text-slate-900">{s.quantity} <span className="text-xs text-slate-500">(min {s.minStock})</span></span>
              </div>
            ))}
          </div>
        </div>

        {/* Mode switch */}
        <div className="flex gap-2 pt-2">
          <Button size="sm" variant={mode === 'adjust' ? 'default' : 'outline'} onClick={() => setMode('adjust')} data-testid="mode-adjust-btn">
            <PackageMinus className="h-4 w-4 mr-1" /> Adjust
          </Button>
          {isAdmin && (
            <Button size="sm" variant={mode === 'add' ? 'default' : 'outline'} onClick={() => setMode('add')} data-testid="mode-add-btn">
              <PackagePlus className="h-4 w-4 mr-1" /> Set level / Add location
            </Button>
          )}
          <Button size="sm" variant={mode === 'transfer' ? 'default' : 'outline'} onClick={() => setMode('transfer')} data-testid="mode-transfer-btn">
            <MoveRight className="h-4 w-4 mr-1" /> Transfer
          </Button>
        </div>

        {/* Form */}
        <div className="space-y-3 pt-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>{mode === 'transfer' ? 'From store' : 'Store'}</Label>
              <Select value={storeId} onValueChange={(v) => { setStoreId(v); setLocationId(''); }}>
                <SelectTrigger className="mt-1.5" data-testid="stock-store-select"><SelectValue placeholder="Select store" /></SelectTrigger>
                <SelectContent>{stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>{mode === 'transfer' ? 'From location' : 'Location'}</Label>
              <Select value={locationId} onValueChange={setLocationId} disabled={!storeId}>
                <SelectTrigger className="mt-1.5" data-testid="stock-location-select"><SelectValue placeholder="Select location" /></SelectTrigger>
                <SelectContent>{locationsFor(storeId).map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {mode === 'transfer' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>To store</Label>
                <Select value={toStoreId} onValueChange={(v) => { setToStoreId(v); setToLocationId(''); }}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select store" /></SelectTrigger>
                  <SelectContent>{stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>To location</Label>
                <Select value={toLocationId} onValueChange={setToLocationId} disabled={!toStoreId}>
                  <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select location" /></SelectTrigger>
                  <SelectContent>{locationsFor(toStoreId).map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}

          {mode === 'adjust' && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Change (delta)</Label>
                  <Input type="number" className="mt-1.5" value={qty} onChange={e => setQty(e.target.value)} data-testid="stock-delta-input" />
                  <div className="text-xs text-slate-500 mt-1">Positive = restock, negative = consume. Currently: {currentQty ?? '—'}</div>
                </div>
                <div>
                  <Label>Reason</Label>
                  <Select value={reason} onValueChange={setReason}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="restock">Restock / delivery</SelectItem>
                      <SelectItem value="consume">Consumed</SelectItem>
                      <SelectItem value="damage">Damaged / written off</SelectItem>
                      <SelectItem value="stock-take">Stock-take correction</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}

          {mode === 'add' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Quantity at this location</Label>
                <Input type="number" className="mt-1.5" value={qty} onChange={e => setQty(e.target.value)} data-testid="stock-set-qty" />
              </div>
              <div>
                <Label>Local min stock</Label>
                <Input type="number" className="mt-1.5" value={minStock} onChange={e => setMinStock(e.target.value)} />
              </div>
            </div>
          )}

          {mode === 'transfer' && (
            <div>
              <Label>Qty to transfer</Label>
              <Input type="number" className="mt-1.5" value={qty} onChange={e => setQty(e.target.value)} data-testid="transfer-qty-input" />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={submit} className="bg-slate-900 hover:bg-slate-800" data-testid="stock-submit-btn">Apply</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Parts;

// -------- Bulk-edit dialog --------
/**
 * BulkEditPartsDialog — admin patches shared fields across many parts in
 * one go. Every field is optional; only those the admin actively edits
 * get sent to the server.
 *
 * Price has two mutually-exclusive modes:
 *   - Markup % (bump): `+8` adds 8%, `-5` drops 5% — good for supplier
 *     price-rise propagation across a category.
 *   - Absolute: overwrite every selected part's sellPrice to the same
 *     number — rare but useful for consumables with standard pricing.
 */
const BulkEditPartsDialog = ({ open, onOpenChange, count, onSubmit }) => {
  const [category, setCategory] = useState('');
  const [supplier, setSupplier] = useState('');
  const [minStock, setMinStock] = useState('');
  const [priceMode, setPriceMode] = useState('none'); // 'none' | 'markup' | 'absolute'
  const [markupPct, setMarkupPct] = useState('');
  const [sellPrice, setSellPrice] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setCategory(''); setSupplier(''); setMinStock('');
      setPriceMode('none'); setMarkupPct(''); setSellPrice('');
    }
  }, [open]);

  const hasAnyChange = (
    category.trim() || supplier.trim() || minStock !== ''
    || (priceMode === 'markup' && markupPct !== '')
    || (priceMode === 'absolute' && sellPrice !== '')
  );

  const submit = async () => {
    const patch = {};
    if (category.trim()) patch.category = category.trim();
    if (supplier.trim()) patch.supplier = supplier.trim();
    if (minStock !== '' && !isNaN(parseInt(minStock, 10))) {
      patch.minStock = parseInt(minStock, 10);
    }
    if (priceMode === 'markup' && markupPct !== '') {
      const n = parseFloat(markupPct);
      if (!isNaN(n)) patch.markupPct = n;
    } else if (priceMode === 'absolute' && sellPrice !== '') {
      const n = parseFloat(sellPrice);
      if (!isNaN(n)) patch.sellPrice = n;
    }
    if (Object.keys(patch).length === 0) return;
    setSaving(true);
    try {
      await onSubmit(patch);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="bulk-edit-dialog">
        <DialogHeader>
          <DialogTitle>Bulk edit {count} part{count === 1 ? '' : 's'}</DialogTitle>
          <DialogDescription>
            Only the fields you fill in are applied. Leave others blank to keep them unchanged.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Price — tabbed between markup and absolute so the two can't be sent
              together (the backend rejects that combo too). */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Sell price</Label>
            <div className="flex gap-1 mt-1 mb-2 bg-slate-100 rounded-md p-1 text-xs">
              {['none', 'markup', 'absolute'].map(m => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setPriceMode(m)}
                  data-testid={`bulk-price-mode-${m}`}
                  className={`flex-1 py-1.5 rounded ${priceMode === m ? 'bg-white shadow-sm font-medium text-slate-900' : 'text-slate-500'}`}
                >
                  {m === 'none' ? 'Keep' : m === 'markup' ? 'Markup %' : 'Set price'}
                </button>
              ))}
            </div>
            {priceMode === 'markup' && (
              <div className="relative">
                <Input
                  type="number"
                  step="0.5"
                  placeholder="+10 (percentage bump)"
                  value={markupPct}
                  onChange={e => setMarkupPct(e.target.value)}
                  data-testid="bulk-markup-pct"
                  className="pr-8"
                />
                <Percent className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <div className="text-[10px] text-slate-500 mt-1">
                  Applied to each part's current sell price. Use negative (e.g. <span className="font-mono">-5</span>) to drop prices.
                </div>
              </div>
            )}
            {priceMode === 'absolute' && (
              <Input
                type="number"
                step="0.01"
                placeholder="e.g. 19.99"
                value={sellPrice}
                onChange={e => setSellPrice(e.target.value)}
                data-testid="bulk-sell-price"
              />
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Category</Label>
            <Input
              className="mt-1"
              placeholder="Leave blank to keep"
              value={category}
              onChange={e => setCategory(e.target.value)}
              data-testid="bulk-category"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Supplier name</Label>
            <Input
              className="mt-1"
              placeholder="Leave blank to keep"
              value={supplier}
              onChange={e => setSupplier(e.target.value)}
              data-testid="bulk-supplier"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Min stock level</Label>
            <Input
              type="number"
              className="mt-1"
              placeholder="Leave blank to keep"
              value={minStock}
              onChange={e => setMinStock(e.target.value)}
              data-testid="bulk-min-stock"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={!hasAnyChange || saving}
            data-testid="bulk-edit-save"
          >
            {saving ? 'Applying…' : `Apply to ${count} part${count === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// -------- Finalise Xero draft dialog --------
const FinalizeDraftDialog = ({ open, onClose, draft, stores, locationsFor, customers, assets, onSaved }) => {
  const [storeId, setStoreId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [qty, setQty] = useState(0);
  const [minStock, setMinStock] = useState(0);
  const [barcode, setBarcode] = useState('');
  const [category, setCategory] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [assetId, setAssetId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !draft) return;
    setStoreId(stores[0]?.id || '');
    setLocationId('');
    setQty(draft.xeroQuantityOnHand || 0);
    setMinStock(0);
    setBarcode(draft.barcode || '');
    setCategory(draft.category === 'From Xero' ? '' : (draft.category || ''));
    setCustomerId('');
    setAssetId('');
  }, [open, draft, stores]);

  if (!draft) return null;

  const assetsForCustomer = customerId
    ? assets.filter(a => a.customerId === customerId)
    : assets;

  const submit = async () => {
    if (!storeId || !locationId) return toast.error('Choose a store and location');
    setSaving(true);
    try {
      await finalizeDraftPart(draft.id, {
        storeId, locationId,
        quantity: Number(qty) || 0,
        minStock: Number(minStock) || 0,
        barcode: barcode || null,
        category: category || null,
        customerId: customerId || null,
        assetId: assetId || null,
      });
      toast.success(`${draft.name} is now live in inventory`);
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Finalise failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Finalise "{draft.name}"</DialogTitle>
          <DialogDescription>
            Tell us where this Xero item lives and who it's for. Once finalised, it's available for work orders.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-mono text-xs text-slate-500">{draft.xeroCode || draft.sku}</div>
              <div className="font-medium text-slate-900">{draft.name}</div>
            </div>
            <div className="text-right text-xs text-slate-600">
              <div>Sell: <span className="font-semibold">£{(draft.sellPrice ?? draft.unitCost ?? 0).toFixed(2)}</span></div>
              {draft.costPrice > 0 && draft.costPrice !== draft.sellPrice && (
                <div>Cost: £{draft.costPrice.toFixed(2)}</div>
              )}
              {draft.xeroIsTracked && <div>Xero QOH: <span className="font-semibold">{draft.xeroQuantityOnHand}</span></div>}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Store *</Label>
              <Select value={storeId} onValueChange={(v) => { setStoreId(v); setLocationId(''); }}>
                <SelectTrigger className="mt-1.5" data-testid="finalize-store-select"><SelectValue placeholder="Select store" /></SelectTrigger>
                <SelectContent>{stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Location *</Label>
              <Select value={locationId} onValueChange={setLocationId} disabled={!storeId}>
                <SelectTrigger className="mt-1.5" data-testid="finalize-location-select"><SelectValue placeholder="Select location" /></SelectTrigger>
                <SelectContent>{locationsFor(storeId).map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Opening quantity</Label>
              <Input type="number" className="mt-1.5" value={qty} onChange={e => setQty(e.target.value)} data-testid="finalize-qty-input" />
              {draft.xeroIsTracked && (
                <p className="text-xs text-slate-500 mt-1">Default is Xero's quantity-on-hand.</p>
              )}
            </div>
            <div>
              <Label>Local min stock</Label>
              <Input type="number" className="mt-1.5" value={minStock} onChange={e => setMinStock(e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Barcode</Label>
              <Input className="mt-1.5" value={barcode} onChange={e => setBarcode(e.target.value)} placeholder="Scan or type (optional)" />
            </div>
            <div>
              <Label>Category</Label>
              <Input className="mt-1.5" value={category} onChange={e => setCategory(e.target.value)} placeholder="e.g. Electrical" />
            </div>
          </div>

          <div>
            <Label>Reserve for customer (optional)</Label>
            <Select value={customerId || 'none'} onValueChange={v => { const val = v === 'none' ? '' : v; setCustomerId(val); setAssetId(''); }}>
              <SelectTrigger className="mt-1.5"><SelectValue placeholder="— Generic stock —" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Generic stock (no customer) —</SelectItem>
                {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Reserve for asset (optional)</Label>
            <Select value={assetId || 'none'} onValueChange={v => setAssetId(v === 'none' ? '' : v)} disabled={assetsForCustomer.length === 0}>
              <SelectTrigger className="mt-1.5">
                <SelectValue placeholder={assetsForCustomer.length === 0 ? 'No assets for this customer' : '— None —'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {assetsForCustomer.map(a => <SelectItem key={a.id} value={a.id}>{a.name} ({a.location})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="finalize-submit-btn">
            {saving ? 'Saving...' : 'Finalise & add to inventory'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
